import { ChangeEmitter } from '@/application/ChangeEmitter';
import type { IPrintZoneEngine2D } from '@/application/ports/IPrintZoneEngine2D';
import type { IProductImageRepository } from '@/application/ports/IProductImageRepository';
import type { IProductStateRepository } from '@/application/ports/IProductStateRepository';
import type { ITemplateConfigurationRepository } from '@/application/ports/ITemplateConfigurationRepository';
import type { TSide } from '@/domain/shared/TSide';

class PrintZoneController {
    constructor(
        private readonly _engine: IPrintZoneEngine2D,
        private readonly _templateConfigurationRepository: ITemplateConfigurationRepository,
        private readonly _productStateRepository: IProductStateRepository,
        private readonly _productImageRepository: IProductImageRepository,
        private readonly _side: TSide,
        private readonly _changeEmitter: ChangeEmitter,
    ) {}

    createGrid(url: string): Promise<void> {
        return this._engine.createGrid(url);
    }

    deleteGrid(): void {
        this._engine.deleteGrid();
        this._productStateRepository.patchProduct(this._side === 'recto' ? { helpGridRecto: null } : { helpGridVerso: null });
        this._changeEmitter.emit();
    }

    resetSurface(): void {
        this._engine.resetSurface();
    }

    setBackgroundColor(color: string): void {
        this._templateConfigurationRepository.setBackgroundColor(this._side, color);
        this._engine.setBackgroundColor(color);
        this._changeEmitter.emit();
    }

    setGridVisible(visible: boolean): void {
        this._engine.setGridVisible(visible);
        this._changeEmitter.emit();
    }

    async setGrid(url: string, uuid: string): Promise<void> {
        await this._engine.createGrid(url);
        this._engine.setGridVisible(true);
        this._productStateRepository.patchProduct(this._side === 'recto' ? { helpGridRecto: uuid } : { helpGridVerso: uuid });
        this._changeEmitter.emit();
    }

    async syncFromState(params: { gridVisible: boolean }): Promise<void> {
        const backgroundColor = this._templateConfigurationRepository.getBackgroundColor(this._side);
        this._engine.setBackgroundColor(backgroundColor);

        const product = this._productStateRepository.getProduct();
        const uuid = this._side === 'recto' ? product?.helpGridRecto : product?.helpGridVerso;
        if (uuid) {
            const blob = await this._productImageRepository.get(uuid);
            await this._engine.createGrid(URL.createObjectURL(blob));
            this._engine.setGridVisible(params.gridVisible);
        }
    }
}

export { PrintZoneController };
